import { useCallback } from 'react';
import { StyleSheet, View } from 'react-native';
import { Logo, Button, theme } from '@socrait/react';
import { useNavigate } from 'react-router';

import { useLogoutMutation } from '~/services/useLogoutMutation';

import { WelcomeSlide } from './WelcomeSlide';
import { TutorialSlide } from './TutorialSlide';
import { PrivacySlide } from './PrivacySlide';

type FirstRunStep = 'welcome' | 'tutorial' | 'privacy';

interface FirstRunScreenProps {
  currentStep: FirstRunStep;
}

export function FirstRunScreen({ currentStep }: FirstRunScreenProps) {
  const navigate = useNavigate();
  const { mutate: logout } = useLogoutMutation();

  const renderSlide = () => {
    switch (currentStep) {
      case 'welcome':
        return <WelcomeSlide />;
      case 'tutorial':
        return <TutorialSlide />;
      case 'privacy':
        return <PrivacySlide />;
      default:
        return <WelcomeSlide />;
    }
  };

  const onPressBack = useCallback(() => {
    if (currentStep === 'welcome') {
      logout();
    } else if (currentStep === 'tutorial') {
      navigate('/setup?step=welcome');
    } else {
      navigate('/setup?step=tutorial');
    }
  }, [currentStep, logout, navigate]);

  const onPressNext = useCallback(() => {
    if (currentStep === 'welcome') {
      navigate('/setup?step=tutorial');
    } else if (currentStep === 'tutorial') {
      navigate('/setup?step=privacy');
    } else if (currentStep === 'privacy') {
      navigate('/setup?step=teacher');
    }
  }, [currentStep, navigate]);

  return (
    <View style={styles.wrapper}>
      <View style={styles.container}>
        <Logo height={38} width={38} />
        <View style={styles.contentContainer}>{renderSlide()}</View>
        <View style={styles.buttonContainer}>
          <Button name="Back" onPress={onPressBack} telemetry-label="first-run-back" />
          <Button name="Next" onPress={onPressNext} telemetry-label="first-run-next" variant="primary" />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    flex: 1,
  },
  container: {
    flex: 1,
    gap: theme.spacing.xlarge,
    padding: theme.spacing.xxlarge,
    zIndex: 0,
  },
  contentContainer: {
    flex: 1,
    alignItems: 'center',
  },
  buttonContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
});
